import axios, { AxiosPromise } from 'axios';
import Venue from '../models/Venue';
import Order from '../models/Order';
import Payment from '../models/Payment';
import Customer from '../models/Customer';
import MyOrdersResponse from '../models/MyOrdersResponse';
import ArticleCategory from '../models/ArticleCategory';
import OptionGroup from '../models/OptionGroup';

import { orderBody, paymentBody } from '../models/Converter';
import PromoCode from '../models/PromoCode';
import { Legal } from '../models/Legal';
import { AuthStrategy } from '../models/AuthStrategy';
import { CustomerAuth } from '../models/CustomerAuth';
import { EventEmitter } from '@angular/core';
import { environment } from '../../environments/environment';
import CheckoutRecommendation from '../models/CheckoutRecommendation';
import { sanitizeId } from '../utils/utils';
import Table from '../models/Table';
import { OrderType } from '../enums/OrderType';
import { Company } from '../models/CompanyCode';
import { CNCDiscount } from '../models/CNCDiscount';
import { ChecksResponse } from '../models/ChecksResponse';
import PiggyReward from '../models/PiggyReward';
import { LoyaltyResponse } from '../models/LoyaltyResponse';

const ORDER_SERVICE = '/v2/order/';
const CUSTOMER_SERVICE = '/v1/customer/';
const GENERAL_SERVICE = '/v1/general/';
const PAYMENT_SERVICE = '/v3/payment/';
const SIMPHONY_SERVICE_V2 = '/v2/simphony/';
const PAYMENT_SERVICE_MULTIPLE = '/v3/payment/multiple';
const DND_SERVICE = '/v1/dnd/';
const PAY_SESSION_SERVICE = '/v1/paysession/';
const PIGGY_SERVICE = '/v1/piggy/';

const VENUE = 'venue/';

const CUSTOMER_ENDPOINT = CUSTOMER_SERVICE;
const VENUE_ENDPOINT = GENERAL_SERVICE + VENUE;

const ORDER_ENDPOINT = ORDER_SERVICE;
const TEST_ORDER_ENDPOINT = ORDER_SERVICE + 'test/';
const CUSTOMER_LOGIN_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/login';
const CUSTOMER_LOGOUT_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/logout';
const CUSTOMER_REFRESH_AUTH_TOKEN_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/token/refresh';
const MAINTENANCE_ENDPOINT = GENERAL_SERVICE + 'maintenance/status';
const PAYMENT_CVV = '/v2/payment/ccv/create/page/';

const authHeader = 'Authorization';

interface PaginatedData<T> {
	data: T[];
	pagination: {
		$current: string;
		pages: string[];
		total: number;
	};
	showing: number;
	total: number;
}

class ApiService {
	axios = axios.create();

	onTokenRefreshed = new EventEmitter<CustomerAuth>();
	authToken: string;
	refreshToken: string;

	constructor() {
		this.axios.defaults.baseURL = environment.baseUrl;
		this.axios.interceptors.request.use(
			async config => {
				if (config.url === TEST_ORDER_ENDPOINT) {
					return config;
				}
				if (this.authToken) {
					config.headers[authHeader] = 'Bearer ' + this.authToken;
				}
				return config;
			},
			error => Promise.reject(error)
		);
		this.axios.interceptors.response.use(
			response => response,
			error => {
				const originalRequest = error.config;
				if (error.response.status === 401) {
					if (
						originalRequest.url === TEST_ORDER_ENDPOINT ||
						originalRequest.url === CUSTOMER_LOGOUT_ENDPOINT ||
						originalRequest.url === CUSTOMER_LOGIN_ENDPOINT
					) {
						return Promise.reject(error);
					}
					if (originalRequest.url === CUSTOMER_REFRESH_AUTH_TOKEN_ENDPOINT) {
						console.error('auth tokes could not be reissued');
						this.onTokenRefreshed.emit(null);
						return Promise.reject(error);
					} else if (!originalRequest._retry && this.onTokenRefreshed && this.refreshToken) {
						console.log('refreshing auth token with refreshToken');
						originalRequest._retry = true;
						return this.refreshAuthToken()
							.then(res => {
								const customerAuth: CustomerAuth = res.data;
								this.authToken = customerAuth.token;
								this.refreshToken = customerAuth.refreshToken;
								this.onTokenRefreshed.emit(customerAuth);
								originalRequest.headers[authHeader] = 'Bearer ' + this.authToken;
								return this.axios(originalRequest);
							})
							.catch(refreshError => {
								this.onTokenRefreshed.emit(null);
								return Promise.reject(refreshError);
							});
					}
					this.onTokenRefreshed.emit(null);
				}
				return Promise.reject(error);
			}
		);
	}

	public createOrder(order: Order, keepPreorder = false): AxiosPromise<Order> {
		return this.axios.post(ORDER_ENDPOINT, orderBody(order, keepPreorder));
	}

	public createTestOrder(authToken: string, order: Order): AxiosPromise<{ order: Order; payment: string }> {
		if (environment.flavor === 'prodRelease') {
			order.flavor = 'prodDebug';
		}
		return this.axios.post(TEST_ORDER_ENDPOINT + order.venue, orderBody(order), {
			headers: {
				Authorization: 'Bearer ' + authToken,
			},
		});
	}

	public getAllVenues(customerGroup: string): AxiosPromise<Venue[]> {
		let country = '';
		if (environment.location) {
			country = '&country=' + environment.location;
		}
		return this.axios.get(VENUE_ENDPOINT + '?customerGroup=' + customerGroup + country);
	}

	public getVenues(customerGroup: string, lat: number, lng: number, distance: number, articles: boolean): AxiosPromise<Venue[]> {
		return this.axios.get(
			VENUE_ENDPOINT + `?lat=${lat}&lng=${lng}&distance=${distance}&articles=${articles}&customerGroup=${customerGroup}`
		);
	}

	public getVenuesBy(postalCode: string, customerGroup: string): AxiosPromise<Venue[]> {
		return this.axios.get(VENUE_ENDPOINT + '?deliveryPostalCode=' + postalCode + '&customerGroup=' + customerGroup);
	}

	public getLazyVenue(venueId: string, isIncludeCustomerGroup: boolean = false): AxiosPromise<Venue> {
		return this.axios.get(VENUE_ENDPOINT + sanitizeId(venueId, isIncludeCustomerGroup));
	}

	public getCategoriesWithArticles(venue: string, articles: boolean): AxiosPromise<ArticleCategory[]> {
		return this.axios.get(GENERAL_SERVICE + `articlecategory/?venue=${venue}&articles=${articles}`);
	}

	public getOptionGroupsByVenue(venue: string): AxiosPromise<OptionGroup[]> {
		return this.axios.get(GENERAL_SERVICE + 'articleoption/byvenue/' + venue);
	}

	public getCustomerOrders(userUid: string): AxiosPromise<MyOrdersResponse> {
		return this.axios.get(PAYMENT_SERVICE + 'customer/' + sanitizeId(userUid));
	}

	public async createPayment(payment: Payment): Promise<Payment> {
		payment._id = undefined;
		return (await this.axios.post(PAYMENT_SERVICE, paymentBody(payment))).data;
	}

	public async finishPaypalPayment(paypalOrderId: string): Promise<Payment> {
		return (await this.axios.patch(PAYMENT_SERVICE + paypalOrderId + '/paypal/pay')).data;
	}

	public getOrder(order: string): AxiosPromise<Order> {
		return this.axios.get(ORDER_ENDPOINT + order);
	}

	// Customer

	public getCustomer(): AxiosPromise<Customer> {
		return this.axios.get(CUSTOMER_ENDPOINT);
	}

	public signInAnonymous(uid: string): AxiosPromise<CustomerAuth> {
		return this.axios.post(CUSTOMER_LOGIN_ENDPOINT, {
			strategy: AuthStrategy.ANONYMOUS,
			uid: uid ? uid : undefined,
			customerGroup: environment.customerGroup,
		});
	}

	public logout(): AxiosPromise {
		return this.axios.post(CUSTOMER_LOGOUT_ENDPOINT);
	}

	public refreshAuthToken(): AxiosPromise<CustomerAuth> {
		return this.axios.post(CUSTOMER_REFRESH_AUTH_TOKEN_ENDPOINT, {
			refreshToken: this.refreshToken,
		});
	}

	public applyPromoCode(order: Order): AxiosPromise<Order> {
		return this.axios.post(ORDER_ENDPOINT + 'promoCode', orderBody(order));
	}

	public getPromoCode(body: { code: string; venue: string }): AxiosPromise<PromoCode> {
		return this.axios.post(GENERAL_SERVICE + 'promoCode/code', body);
	}

	public async getPromoCodesOfVenue(venue: string, automatic?: boolean): Promise<PromoCode[]> {
		const data = (
			await this.axios.get<PaginatedData<PromoCode>>(GENERAL_SERVICE + `promoCode`, {
				params: {
					venue: venue,
					isAutomatic: automatic,
					limit: 1000,
				},
			})
		).data.data;

		const today = new Date();
		const currentDay = today.getDay(); // Sunday: 0, Monday: 1, ...
		return data.filter(promo => {
			if (!promo.active) {
				return false;
			}

			const fromDate = promo.from ? new Date(promo.from) : null;
			const toDate = promo.to ? new Date(promo.to) : null;

			if (fromDate && today < fromDate) {
				return false;
			}
			if (toDate && today > toDate) {
				return false;
			}

			if (promo.availability?.byWeekday && promo.availability.byWeekday[currentDay] === false) {
				return false;
			}

			return true;
		});
	}
	public async getPromoCodesByPiggyRewardUuids(rewardUuids: string[]): Promise<PromoCode[]> {
		const response = await this.axios.get<PaginatedData<PromoCode>>(GENERAL_SERVICE + `promoCode`, {
			params: {
				piggyRewardUuid: rewardUuids,
			},
		});
		const today = new Date();
		const currentDay = today.getDay(); // Sunday: 0, Monday: 1, ...
		return response.data.data.filter(promo => {
			if (!promo.active) {
				return false;
			}

			const fromDate = promo.from ? new Date(promo.from) : null;
			const toDate = promo.to ? new Date(promo.to) : null;

			if (fromDate && today < fromDate) {
				return false;
			}
			if (toDate && today > toDate) {
				return false;
			}

			return !(promo.availability?.byWeekday && promo.availability.byWeekday[currentDay] === false);
		});
	}

	public getPromoCodesForCustomer(params: {
		userUuid?: string;
		customerGroup?: string;
		venues?: string[];
		isAutomatic?: boolean;
		isVoucher?: boolean;
		piggyRewardUuid?: string;
		piggyContactUuid?: string;
	}): AxiosPromise<PromoCode[]> {
		const requestParams: any = {};
		if (params.userUuid) {
			requestParams.userUuid = params.userUuid;
		}
		if (params.customerGroup) {
			requestParams.customerGroup = params.customerGroup;
		}
		if (params.venues) {
			requestParams.venues = params.venues;
		}
		if (params.isAutomatic !== undefined) {
			requestParams.isAutomatic = params.isAutomatic;
		}
		if (params.isVoucher !== undefined) {
			requestParams.isPiggyVoucher = params.isVoucher;
		}
		if (params.piggyRewardUuid) {
			requestParams.piggyRewardUuid = params.piggyRewardUuid;
		}
		if (params.piggyContactUuid) {
			requestParams.piggyContactUuid = params.piggyContactUuid;
		}

		return this.axios.get(GENERAL_SERVICE + 'promoCode/customer', { params: requestParams });
	}

	public getMembership(venue: string, code: string): AxiosPromise<LoyaltyResponse> {
		return this.axios.get(PIGGY_SERVICE + `contact/jwt?venue=${venue}&identifier=${code}`);
	}
	public getVouchers(venue: string, contactUuid: string) {
		return this.axios.get(PIGGY_SERVICE + `vouchers?venue=${venue}&contact_uuid=${contactUuid}`);
	}

	public getRewards(venue: string, contactUuid: string): AxiosPromise<PiggyReward[]> {
		return this.axios.get(
			PIGGY_SERVICE + `rewards?customerGroup=${environment.customerGroup}&venue=${venue}&contact_uuid=${contactUuid}`
		);
	}

	public isMaintenanceActive(): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				resolve((await this.axios.get(MAINTENANCE_ENDPOINT)).data);
			} catch (e) {
				reject(e);
			}
		});
	}

	public getPayment(payment: string): AxiosPromise<Payment> {
		return this.axios.get(PAYMENT_SERVICE + 'byId/' + payment);
	}

	public getLegal(venue: string): AxiosPromise<Legal> {
		return this.axios.get(VENUE_ENDPOINT + venue + '/legal');
	}

	public getCartRecommendations(body: {
		venue: string;
		articles: { _id: string; quantity: number }[];
		orderType: OrderType;
		subOrderType: string;
	}): AxiosPromise<CheckoutRecommendation[]> {
		return this.axios.post(GENERAL_SERVICE + 'cartrecommendation/result', body);
	}

	public getTable(venue: string, tableNumber: string): AxiosPromise<Table> {
		return this.axios.get(VENUE_ENDPOINT + venue + '/table/' + tableNumber);
	}

	public async createCCVPayment(venueId: string, payment: Payment): Promise<Payment> {
		payment._id = undefined;
		return (await this.axios.post(PAYMENT_CVV + `${venueId}`, paymentBody(payment))).data;
	}

	public async updateOrder(order: Order) {
		return await this.axios.patch(ORDER_ENDPOINT + order._id, order);
	}

	public async validateCompanyCode(code: string): AxiosPromise<Company> {
		return await this.axios.get(DND_SERVICE + 'company/company-code/' + code);
	}

	public async createWorldLinePayment(data: any, venueId: string): Promise<Payment> {
		return (await this.axios.post(PAYMENT_SERVICE + 'worldline/page/' + venueId, data)).data;
	}

	public getPromoCodeByLinkedCustomerGroup(code: string): AxiosPromise<PromoCode> {
		return this.axios.post(GENERAL_SERVICE + 'promoCode/code/byLinkedCustomerGroup', {
			code: code,
			linkedCustomerGroup: environment.customerGroup,
		});
	}

	public async addPersonPromo(data: { customer: string; promoCode: string }) {
		return (await this.axios.patch(CUSTOMER_SERVICE + 'personal-promocodes/add', data)).data;
	}

	public async boundPromoCodeToUser(promoId: string, userId: string) {
		return (await this.axios.patch<PromoCode[]>(GENERAL_SERVICE + 'promoCode/' + promoId + '/bind/' + userId)).data;
	}

	public async createMultyplePayments(payments: Payment[]): Promise<Payment[]> {
		const mapPayments = payments.map(it => {
			it._id = undefined;
			return paymentBody(it);
		});
		return (await this.axios.post(PAYMENT_SERVICE_MULTIPLE, mapPayments)).data;
	}

	public async getCNCDiscountsToday(venue: string): Promise<CNCDiscount[]> {
		return (await this.axios.get(`${SIMPHONY_SERVICE_V2}cnc/${venue}/discounts-today`)).data;
	}
	public async getCNCDiscounts(venue: string): Promise<CNCDiscount[]> {
		return (await this.axios.get(`${SIMPHONY_SERVICE_V2}cnc/${venue}/discounts`)).data;
	}

	public async getChecksOfTable(venue: string, table: string): Promise<ChecksResponse> {
		return (await this.axios.get(PAY_SESSION_SERVICE + 'checks/' + venue + '/' + table)).data;
	}
}

export let Api = new ApiService();
export { PAYMENT_SERVICE };
